import { v4 as uuidv4 } from 'uuid'

export type RevisionIdSuffix = '_fmp' | '_cg'

const STRATA_REVISION_ID_KEY = 'strata_revision_id'
const RECOGNIZED_SUFFIXES_MAP: Record<RevisionIdSuffix, true> = { _fmp: true, _cg: true }
const RECOGNIZED_SUFFIXES = Object.keys(RECOGNIZED_SUFFIXES_MAP) as RevisionIdSuffix[]

// Keyed by storageKey so multiple callers with different keys stay isolated.
// Preserved for the page lifetime so repeated blocked-storage calls return the
// same correlation ID (matches sessionStorage behavior).
const inMemoryFallbackIds = new Map<string, string>()

// Called only from getRevisionId, after the SSR window guard has already run.
function resolveSuffix(): RevisionIdSuffix {
  if (window.location.pathname.includes('find-my-plan')) return '_fmp'
  const router = new URLSearchParams(window.location.search).get('router')
  if (router === 'tg' || router === 'ttpg') return '_fmp'
  return '_cg'
}

function splitSuffix(id: string): [string, RevisionIdSuffix | null] {
  for (const suffix of RECOGNIZED_SUFFIXES) {
    if (id.endsWith(suffix)) {
      return [id.slice(0, id.length - suffix.length), suffix]
    }
  }
  return [id, null]
}

function applyTransition(existing: string, effectiveSuffix: RevisionIdSuffix): string {
  const [base, currentSuffix] = splitSuffix(existing)
  if (currentSuffix === effectiveSuffix) return existing
  return `${base}${effectiveSuffix}`
}

/**
 * Returns the session-scoped revision ID for the current journey.
 *
 * Reads from or writes to sessionStorage under `storageKey`. The UUID base is
 * generated once per session and shared between FMP and grid so downstream
 * analytics can join events across both surfaces while the suffix records the
 * most recent experience.
 *
 * Pass an explicit `suffix` when the call site definitively knows the
 * experience (e.g. QuestionnaireWrapper always passes '_fmp'). When omitted,
 * the suffix is inferred from the URL at the time of the call:
 *   1. pathname contains 'find-my-plan' → _fmp
 *   2. query param router === 'tg' or 'ttpg' → _fmp
 *   3. otherwise → _cg
 *
 * This function mutates the stored last-touch suffix. Call it only from a
 * committed grid or FMP page-load effect, never from render or from cart and
 * confirmation surfaces that must inherit the latest touch.
 *
 * Last-touch transition behavior: if an existing ID ends in a recognized
 * suffix that differs from the effective suffix, the suffix is replaced on the
 * same UUID base and the updated ID is persisted. If the suffix already
 * matches, the existing value is returned without any write. Legacy IDs with
 * no recognized suffix have the effective suffix appended.
 *
 * Pass a custom `storageKey` when you need an isolated revision ID for a
 * different event type — this prevents conflicts with the default strata key.
 *
 * @param suffix - explicit suffix override ('_fmp' | '_cg'). Defaults to URL inference.
 * @param storageKey - sessionStorage key. Defaults to 'strata_revision_id'.
 */
export function getRevisionId(
  suffix?: RevisionIdSuffix,
  storageKey = STRATA_REVISION_ID_KEY,
): string {
  if (typeof window === 'undefined') return ''

  const effectiveSuffix = suffix ?? resolveSuffix()
  let storageRevisionId: string | undefined

  try {
    const existing = sessionStorage.getItem(storageKey)
    if (existing) {
      storageRevisionId = applyTransition(existing, effectiveSuffix)
      if (storageRevisionId === existing) return existing
    } else {
      storageRevisionId = `${uuidv4()}${effectiveSuffix}`
    }

    sessionStorage.setItem(storageKey, storageRevisionId)
    return storageRevisionId
  } catch {
    // sessionStorage may be blocked (third-party cookie restrictions, non-HTTPS
    // partner pages). Fall back to a session-local ID that won't persist across
    // navigations but won't throw either.
    //
    // storageRevisionId is set when getItem succeeded but setItem threw. That
    // storage-derived value is authoritative over any stale in-memory fallback —
    // it carries the real persisted UUID base for this session.
    if (storageRevisionId !== undefined) {
      inMemoryFallbackIds.set(storageKey, storageRevisionId)
      return storageRevisionId
    }

    const existing = inMemoryFallbackIds.get(storageKey)
    if (existing) {
      const transitioned = applyTransition(existing, effectiveSuffix)
      if (transitioned === existing) return existing
      inMemoryFallbackIds.set(storageKey, transitioned)
      return transitioned
    }

    const revisionId = `${uuidv4()}${effectiveSuffix}`
    inMemoryFallbackIds.set(storageKey, revisionId)
    return revisionId
  }
}
